"use client"

import * as React from "react"
import { X as XIcon } from "lucide-react"
import { useMediaQuery } from "@/hooks/use-media-query"
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog"
import { Drawer } from "vaul"
import { cn } from "@/lib/utils"

interface ResponsiveDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: React.ReactNode
  className?: string
  title?: string
  showCloseButton?: boolean
}

/**
 * On mobile (< 768px): renders as a bottom Drawer (vaul) with drag-to-close.
 * On desktop: renders as a centered Dialog (Radix).
 * Emil Kowalski style.
 */
export function ResponsiveDialog({
  open,
  onOpenChange,
  children,
  className,
  title,
  showCloseButton = true,
}: ResponsiveDialogProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)")

  if (isDesktop) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={className}
          showCloseButton={showCloseButton}
        >
          {title && <DialogTitle className="sr-only">{title}</DialogTitle>}
          {children}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Drawer.Root open={open} onOpenChange={onOpenChange}>
      <Drawer.Portal>
        {/* Mobile vaul drawer sits BELOW MobileBottomNav (z-[60]) and
            BELOW Radix Dialog (z-[70]) — that's intentional. The mobile
            UX choice is that the bottom-nav stays interactive while a
            drawer is open. Flows that previously needed vaul-on-Radix
            (insufficient-credits «Пополнить» from inside an effect
            Dialog) now hard-navigate to /account instead of trying to
            stack drawers on Radix dialogs. */}
        <Drawer.Overlay className="fixed inset-0 z-[55] bg-foreground/40 backdrop-blur-sm" />
        <Drawer.Content
          className={cn(
            "fixed inset-x-0 z-[58] flex flex-col bg-background border-t border-border rounded-t-2xl",
            // Mobile: pin the drawer between the sticky header and the
            // MobileBottomNav so both stay visible / interactive. Drawer
            // sits flush against the bottom-nav top edge (no gap), and
            // max-h leaves the sticky header (~64px) showing above.
            "bottom-[calc(64px+env(safe-area-inset-bottom))]",
            "max-h-[calc(100dvh-64px-64px-env(safe-area-inset-bottom)-env(safe-area-inset-top,0px))]",
            className
          )}
        >
          {/* Drag Handle */}
          <div className="mx-auto mt-3 mb-2 h-1.5 w-12 shrink-0 rounded-full bg-muted-foreground/20" />

          {showCloseButton && (
            <Drawer.Close
              aria-label="Закрыть"
              className="absolute top-3 right-3 z-20 h-9 w-9 inline-flex items-center justify-center rounded-full text-foreground bg-muted hover:bg-muted/80 active:scale-95 transition-all shadow-sm border border-border/40"
            >
              <XIcon className="h-4 w-4" />
              <span className="sr-only">Закрыть</span>
            </Drawer.Close>
          )}

          <Drawer.Title className="sr-only">{title || "Dialog"}</Drawer.Title>

          <div className="flex-1 overflow-y-auto px-4 pb-[max(2rem,env(safe-area-inset-bottom))]">
            {children}
          </div>
        </Drawer.Content>
      </Drawer.Portal>
    </Drawer.Root>
  )
}
